<template>
  <div v-if="target">
    <q-card-section>
      <target-card
        ref="target"
        v-model="programTargets"
        :program="program"
        :program-workflow="programWorkflow"
        :target-id="targetId"
        :force-show-discriminative-stimulus="targetsHasDiscriminativeStimulus"
        target-data-open
        :selectable="false"
        :editable="false"
        :disable="!editable"
        :enter-adds-target="false"
      />
    </q-card-section>
    <q-card-actions
      v-if="editable && !hideActions"
      class="row"
    >
      <q-space />
      <hr-dismiss-btn
        :label="$t('common.buttonCancel')"
        @click="onCancelClick"
      />
      <hr-confirm-btn
        :label="$t('common.buttonSave')"
        :loading="showSpinner"
        data-e2e="button-save"
        @click="onOKClick"
      />
    </q-card-actions>
  </div>
</template>
<script>
// NOTE: This component stays on the Options API because of its save flow
// (saveTargetChanges / updateTargetsPerStatus / getActiveWorkflow) only exists on the
// Targets mixin for now. Refactor to <script setup> + a composable once those are extracted.
import TargetCard from 'components/Programs/TargetCard';
import { find } from 'lodash';
import { mapActions } from 'pinia';
import { notifyNegative, notifySuccess } from 'src/composables/interactions';
import { HrConfirmBtn, HrDismissBtn } from 'src/design-system';
import Targets from 'src/mixins/Targets';
import { programsTargets } from 'src/store/programsTargets';
import { logEvent } from 'src/utils/LogEvent';

export default {
  name: 'TargetDetailsContent',

  components: { TargetCard, HrConfirmBtn, HrDismissBtn },

  mixins: [Targets],

  props: {
    program: {
      type: Object,
      required: true,
    },

    targetId: {
      type: String,
      required: true,
    },

    editable: {
      type: Boolean,
      default: true,
    },

    // Hides the built-in Save/Cancel buttons
    hideActions: {
      type: Boolean,
      default: false,
    },
  },

  emits: ['ok', 'cancel', 'dirty-change'],

  data() {
    return {
      showSpinner: false,
      programTargets: [],
    };
  },

  computed: {
    target() {
      return find(this.programTargets, { id: this.targetId });
    }, // target()

    targetsHasDiscriminativeStimulus() {
      // @todo Check if it has
      return true;
    }, // targetsHasDiscriminativeStimulus()

    programWorkflow() {
      return this.getActiveWorkflow(this.program, 'workflow');
    }, // programWorkflow()

    hasChanges() {
      return this.programTargets.some((target) => Boolean(target.editStatus));
    }, // hasChanges()
  },

  watch: {
    targetId() {
      this.reloadTargets(this.program.id);
    },

    hasChanges: {
      immediate: true,
      handler(hasChanges) {
        this.$emit('dirty-change', hasChanges);
      },
    },
  },

  created() {
    this.reloadTargets(this.program.id);
  },

  methods: {
    ...mapActions(programsTargets, ['getAllProgramTargets', 'invalidateProgramTargetsCache']),

    async onOKClick() {
      try {
        await this.saveTarget();
        notifySuccess(this.$t('editProgram.notifySuccessTargetUpdated'));
      } catch (error) {
        console.error(error);
        return;
      }

      this.$emit('ok');
    }, // onOKClick()

    onCancelClick() {
      this.$emit('cancel');
    }, // onCancelClick()

    async reloadTargets(programId) {
      this.$q.loading.show();
      try {
        this.programTargets = await this.getAllProgramTargets({ program: programId });
      } catch (err) {
        notifyNegative(this.$t('common.somethingWentWrong'));
        logEvent(err);
      } finally {
        this.$q.loading.hide();
      }
    }, // reloadTargets()

    async saveTarget() {
      this.$q.loading.show();
      try {
        await this.saveTargetChanges(this.programTargets, this.program.id);
        this.invalidateProgramTargetsCache({ program: this.program.id });
        await this.updateTargetsPerStatus(this.program);
      } catch (err) {
        notifyNegative(this.$t('common.somethingWentWrongError'));
        logEvent(err);
      } finally {
        this.$q.loading.hide();
      }
    }, // saveTarget()
  },
};
</script>
